<script lang="ts">
  import { browser } from '$app/environment'
  import {
    Accordion,
    AccordionItem,
    getModalStore,
    getToastStore,
    popup,
    type PopupSettings,
  } from '@skeletonlabs/skeleton'
  import posthog from 'posthog-js'
  import { onMount, type SvelteComponent } from 'svelte'

  import HuggingFaceDatasetFields from './HuggingFaceDatasetFields.svelte'
  import {
    applySharedTokenToCustomDatasets,
    cloneHuggingFaceDataset,
    getCustomHuggingFaceDatasets,
    persistCustomDatasetTokens,
    SharedCustomDatasetTokenState,
  } from './run-llm-unlearning-huggingface-datasets.svelte'
  import {
    createRunLlmRestoreState,
    isRunLlmRestoreState,
    restoreRunLlmState,
  } from './run-llm-unlearning-restore-state.svelte'
  import SharedCustomDatasetTokenSection from './SharedCustomDatasetTokenSection.svelte'
  import {
    type ICreateLlm,
    isHuggingFaceTransformersSource,
    isLocalTransformersSource,
  } from '../llms/llms-store.svelte'
  import { getRestrictedModelConfig } from '../llms/restricted-models'
  import {
    BehaviorType,
    Bias,
    BiasType,
    CustomUtility,
    DatasetType,
    displayDatasetType,
    getBehaviorByType,
    getBiasByType,
    HallucinationType,
    hallucinationTypeLabels,
    hallucinationTypeValues,
    HuggingFaceDataset,
    type IDataset,
    RunInfo,
    SecurityBehavior,
    UnlearningLlmAdvancedOptions,
    UnlearnType,
  } from '../runs/llm-run.svelte'
  import CloseModalButton from '@components/CloseModalButton.svelte'
  import Input from '@components/Input.svelte'
  import MetadataFields from '@components/MetadataFields.svelte'
  import Select from '@components/Select.svelte'
  import { DatasetMetadataType } from '@routes/data-qa/datasets/dataset-metadata-type'
  import { displayMetadataTypes } from '@routes/data-qa/datasets/datasets-store.svelte'
  import {
    createSaveHuggingFaceTokenMutation,
    loadHuggingFaceTokens,
  } from '@routes/huggingface-tokens/huggingface-tokens-store'
  import { getStoragePrefix, StorageType } from '@routes/storage-configs/storage-configs-store'
  import { loggedInStore } from '@stores/logged-in-store'
  import { type Config, getConfig } from '@utils/config.svelte'
  import { LLM_UNLEARNING_ADVANCED_TOKEN_OPTIONS_FLAG } from '@utils/feature-flags'
  import { HirundoCSV } from '@utils/hirundo-csv.svelte'
  import { setRestoreState } from '@utils/modal-restore'
  import { inferParameterCountFromIdentifier } from '@utils/model-parameter-count'
  import { getLlmTierValidationMessage, type LlmTieredProduct, Product } from '@utils/product'
  import { queryClient } from '@utils/svelte-query-client'
  import { ValidityState } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'
  import NounBehavior from '$lib/components/icons/NounBehavior.svelte'
  import NounRemoveRow from '$lib/components/icons/NounRemoveRow.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  type AccordionEvent = CustomEvent<{
    event?: Event
    id: string
    panelId: string
    open: boolean
    autocollapse: boolean
  }>

  interface PosthogWindow extends Window {
    __posthog_enabled?: boolean
  }

  let { parent }: Props = $props()

  const primaryOrganizationId = $loggedInStore.primaryOrganizationId
  let runInfo = $state(new RunInfo(primaryOrganizationId))
  let config = $state<Config | null>(null)
  let configLoadFailed = $state(false)
  const restrictedModelConfig = $derived(getRestrictedModelConfig(config))
  const restrictedModelIds = $derived(restrictedModelConfig.restrictedModelIds)
  const isRestrictedMode = $derived(restrictedModelConfig.isRestrictedMode)
  const enabledBehaviors = $derived(
    (config?.enabledUnlearningBehaviors ?? []).filter((behavior) =>
      [
        BehaviorType.BIAS,
        BehaviorType.SECURITY,
        BehaviorType.REFUSAL,
        BehaviorType.CUSTOM,
      ].includes(behavior as BehaviorType),
    ),
  )
  const defaultBehaviorType = $derived(enabledBehaviors[0])
  const createDefaultBehavior = () =>
    defaultBehaviorType === BehaviorType.BIAS
      ? getBiasByType(BiasType.ALL)
      : defaultBehaviorType
        ? getBehaviorByType(defaultBehaviorType as BehaviorType, { biasType: BiasType.ALL })
        : null
  const INITIAL_INVALID_STATE = {
    datasetCsvUrl: new ValidityState(),
    unbiasedDatasetCsvUrl: new ValidityState(),
    biasedDatasetCsvUrl: new ValidityState(),
    utilityDatasetCsvUrl: new ValidityState(),
    huggingFaceDatasetName: new ValidityState(),
    unbiasedHuggingFaceDatasetName: new ValidityState(),
    biasedHuggingFaceDatasetName: new ValidityState(),
    utilityHuggingFaceDatasetName: new ValidityState(),
  }
  let invalids = $state([INITIAL_INVALID_STATE])

  let useAdvancedRunOptions = $state(false)
  let advancedTokenOptionsEnabled = $state(false)
  let advancedTokenOptionsReady = $state(false)

  let unlearnType = $state<UnlearnType | null>(UnlearnType.BEHAVIOR)
  //  ⬆️ Defaulting to behavior unlearning for now, since unlearning data is not yet ready

  let multipleUnlearnTypes = $state(false)

  const shouldShowAdvancedRunOptions = $derived.by(
    () => advancedTokenOptionsEnabled && advancedTokenOptionsReady,
  )

  $effect(() => {
    if (!advancedTokenOptionsReady || shouldShowAdvancedRunOptions) {
      return
    }
    useAdvancedRunOptions = false
    runInfo.advancedOptions = null
  })

  $effect(() => {
    const setUnlearnTypeFromConfig = async () => {
      try {
        config = await getConfig()
      } catch (error) {
        console.error('Failed to load unlearning configuration', error)
        configLoadFailed = true
        return
      }
      if (
        config.activeProducts?.includes(Product.UNLEARNING_LLM_DATA) &&
        config.activeProducts?.includes(Product.UNLEARNING_LLM_BEHAVIOR)
      ) {
        unlearnType = null
        multipleUnlearnTypes = true
        // Trigger picker for unlearning type
      } else if (config.activeProducts?.includes(Product.UNLEARNING_LLM_DATA)) {
        unlearnType = UnlearnType.DATA
        multipleUnlearnTypes = false
      } else if (config.activeProducts?.includes(Product.UNLEARNING_LLM_BEHAVIOR)) {
        unlearnType = UnlearnType.BEHAVIOR
        multipleUnlearnTypes = false
      }
    }
    setUnlearnTypeFromConfig()
  })

  let runInfoFormRef = $state<HTMLFormElement | null>(null)
  let advancedOptionsFormRef = $state<HTMLFormElement | null>(null)

  const modalStore = getModalStore()
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey
  const selectedModel = $derived(($modalStore[0]?.value ?? null) as ICreateLlm | null)
  const selectedModelSourceName = $derived.by(() => {
    if (!selectedModel) {
      return ''
    }
    if (isHuggingFaceTransformersSource(selectedModel.modelSource)) {
      return selectedModel.modelSource.modelName
    }
    return ''
  })
  const restrictedModelSelection = $derived.by(() => {
    if (restrictedModelIds.length === 0) {
      return ''
    }
    if (restrictedModelIds.includes(selectedModelSourceName)) {
      return selectedModelSourceName
    }
    return restrictedModelIds[0] ?? ''
  })

  const toastStore = getToastStore()
  let activeToast: string | null = null
  const saveTokenMutation = createSaveHuggingFaceTokenMutation(queryClient)
  const sharedCustomDatasetTokenState = new SharedCustomDatasetTokenState()
  let hasAppliedRestoreState = $state(false)
  let restoreWarning = $state<string | null>(null)

  type RunOptionsMeta = {
    llmId: number
    llmName: string
    modelSource?: unknown
  }

  const runOptions = $derived(($modalStore[0]?.meta?.runOptions ?? null) as RunOptionsMeta | null)
  const canReturnToRunOptions = $derived(Boolean(runOptions))

  const onBackToRunOptions = () => {
    if (!$modalStore[0]) {
      return
    }
    $modalStore[0].response?.('back-to-run-options')
    modalStore.close()
  }

  $effect(() => {
    if (hasAppliedRestoreState) {
      return
    }
    if (config === null) {
      return
    }
    if (
      !$modalStore[0]?.meta?.restoreState ||
      !isRunLlmRestoreState($modalStore[0].meta.restoreState)
    ) {
      return
    }
    const restoredState = restoreRunLlmState($modalStore[0].meta.restoreState, enabledBehaviors)
    unlearnType = restoredState.unlearnType
    multipleUnlearnTypes = restoredState.multipleUnlearnTypes
    runInfo = restoredState.runInfo
    restoreWarning = restoredState.warning
    hasAppliedRestoreState = true
  })

  onMount(() => {
    void loadHuggingFaceTokens().catch(() => undefined)
  })

  onMount(() => {
    const posthogWindow = window as PosthogWindow
    if (!browser || !posthogWindow.__posthog_enabled) {
      advancedTokenOptionsEnabled = false
      advancedTokenOptionsReady = true
      return
    }
    const updateAdvancedTokenOptionsFlag = () => {
      advancedTokenOptionsEnabled = Boolean(
        posthog.isFeatureEnabled(LLM_UNLEARNING_ADVANCED_TOKEN_OPTIONS_FLAG),
      )
      advancedTokenOptionsReady = true
    }
    const cleanup = posthog.onFeatureFlags(updateAdvancedTokenOptionsFlag)
    return () => {
      if (typeof cleanup === 'function') {
        cleanup()
      }
    }
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    setRestoreState(
      restoreKey,
      createRunLlmRestoreState({
        unlearnType,
        multipleUnlearnTypes,
        runInfo,
      }),
    )
  })

  const onFormCancel = () => {
    if ($modalStore[0].response) {
      $modalStore[0].response(false)
    }
    modalStore.close()
  }
  const getSelectedRunProduct = (): LlmTieredProduct | null => {
    if (unlearnType === UnlearnType.BEHAVIOR) {
      return Product.UNLEARNING_LLM_BEHAVIOR
    }
    if (unlearnType === UnlearnType.DATA) {
      return Product.UNLEARNING_LLM_DATA
    }
    return null
  }

  const validateSelectedModelTierForRun = async (): Promise<boolean> => {
    if (!selectedModel) {
      return true
    }

    let modelIdentifier = ''
    if (isHuggingFaceTransformersSource(selectedModel.modelSource)) {
      modelIdentifier = selectedModel.modelSource.modelName.trim()
    } else if (isLocalTransformersSource(selectedModel.modelSource)) {
      modelIdentifier = selectedModel.modelSource.localPath.trim()
    }
    if (!modelIdentifier) {
      return true
    }

    const parameterCount = await inferParameterCountFromIdentifier(modelIdentifier)
    const selectedRunProduct = getSelectedRunProduct()
    const tierValidationMessage = getLlmTierValidationMessage({
      parameterCount,
      activeProductEntitlements: config?.activeProductEntitlements ?? config?.activeProducts ?? [],
      candidateProducts: selectedRunProduct ? [selectedRunProduct] : [],
    })
    if (!tierValidationMessage) {
      return true
    }

    activeToast = toastStore.trigger({
      message: tierValidationMessage,
      background: 'variant-filled-error',
    })
    return false
  }

  const onFormSubmit = async () => {
    if (config === null) {
      return
    }
    if (
      unlearnType === UnlearnType.BEHAVIOR &&
      !enabledBehaviors.includes(runInfo.targetBehaviors[0]?.type ?? '')
    ) {
      return
    }
    if (
      unlearnType === UnlearnType.BEHAVIOR &&
      runInfo.targetBehaviors[0]?.type !== BehaviorType.REFUSAL &&
      runInfo.aggressiveness === null
    ) {
      runInfo.aggressiveness = 1
    }
    if (!runInfoFormRef?.reportValidity()) {
      console.error('Run info form is invalid')
      return
    }
    if (useAdvancedRunOptions && !advancedOptionsFormRef?.reportValidity()) {
      console.error('Advanced options form is invalid')
      return
    }
    if (activeToast) {
      toastStore.close(activeToast)
      activeToast = null
    }

    if (!(await validateSelectedModelTierForRun())) {
      return
    }

    await persistCustomDatasetTokens({
      datasets: customHuggingFaceDatasets,
      sharedTokenState: sharedCustomDatasetTokenState,
      mutation: $saveTokenMutation,
    })

    if ($modalStore[0].response) {
      await $modalStore[0].response($state.snapshot(runInfo))
    }
    modalStore.close()
  }

  const accordionChange = (event: AccordionEvent) => {
    useAdvancedRunOptions = event.detail.open
  }

  const changeUnlearnType = (type: UnlearnType | null) => {
    unlearnType = type
    runInfo.targetBehaviors = []
    runInfo.targetUtilities = []
    if (type === UnlearnType.BEHAVIOR) {
      const defaultBehavior = createDefaultBehavior()
      if (defaultBehavior) {
        runInfo.targetBehaviors.push(defaultBehavior)
      }
    } else if (type === UnlearnType.DATA) {
      runInfo.targetUtilities.push(new CustomUtility(new HuggingFaceDataset()))
    }
  }

  const syncInvalidCount = (expectedCount: number) => {
    if (invalids.length === expectedCount) {
      return
    }
    invalids = Array.from(
      {
        length: expectedCount,
      },
      (_, index) => invalids[index] ?? INITIAL_INVALID_STATE,
    )
  }

  const addBehaviorCustomUtility = () => {
    runInfo.targetUtilities.push(new CustomUtility(new HuggingFaceDataset()))
    syncInvalidCount(1 + runInfo.targetUtilities.length)
  }

  const removeBehaviorCustomUtility = (customUtilityIndex: number) => {
    runInfo.targetUtilities.splice(customUtilityIndex, 1)
    syncInvalidCount(1 + runInfo.targetUtilities.length)
  }

  $effect(() => {
    if (unlearnType !== UnlearnType.BEHAVIOR) {
      return
    }
    if (!enabledBehaviors.includes(runInfo.targetBehaviors[0]?.type ?? '')) {
      const defaultBehavior = createDefaultBehavior()
      if (defaultBehavior || runInfo.targetBehaviors.length > 0) {
        runInfo.targetBehaviors = defaultBehavior ? [defaultBehavior] : []
      }
    }
    if (runInfo.targetBehaviors.length > 1) {
      runInfo.targetBehaviors = [runInfo.targetBehaviors[0]]
      invalids = [invalids[0] ?? INITIAL_INVALID_STATE]
    }
    syncInvalidCount(1 + runInfo.targetUtilities.length)
    const behavior = runInfo.targetBehaviors[0]
    if (!behavior) {
      return
    }
    if (behavior instanceof Bias && behavior.biasType !== BiasType.ALL) {
      runInfo.targetBehaviors[0] = getBiasByType(BiasType.ALL)
    }
    if (behavior.type === BehaviorType.REFUSAL) {
      if (runInfo.targetUtilities.length > 0) {
        runInfo.targetUtilities = []
      }
      if (runInfo.aggressiveness !== null) {
        runInfo.aggressiveness = null
      }
    } else if (runInfo.aggressiveness === null) {
      runInfo.aggressiveness = 1
    }
  })

  const behaviorCustomUtilities = $derived.by(() =>
    unlearnType === UnlearnType.BEHAVIOR ? runInfo.targetUtilities : [],
  )

  const getInvalidBehaviorHover = (target: string): PopupSettings => ({
    event: 'hover',
    placement: 'right',
    target,
  })

  const MAX_TOKENS_FOR_MODEL = 512

  const customHuggingFaceDatasets = $derived.by(() => getCustomHuggingFaceDatasets(runInfo))
  const hasCustomHuggingFaceDatasets = $derived(customHuggingFaceDatasets.length > 0)

  $effect(() => {
    if (!hasCustomHuggingFaceDatasets) {
      sharedCustomDatasetTokenState.clear()
      return
    }
    if (!sharedCustomDatasetTokenState.useToken) {
      return
    }
    applySharedTokenToCustomDatasets(customHuggingFaceDatasets, sharedCustomDatasetTokenState)
  })

  $effect(() => {
    if (!sharedCustomDatasetTokenState.useToken && sharedCustomDatasetTokenState.saveToken) {
      sharedCustomDatasetTokenState.saveToken = false
      sharedCustomDatasetTokenState.tokenName = ''
    }
  })
</script>

{#snippet modelNoticeIfNecessary(
  isRestrictedMode: boolean,
  restrictedModelIds: string[],
  restrictedModelSelection: string,
  selectedModelName: string,
)}
  {@const modelName =
    isRestrictedMode && restrictedModelIds.length > 0
      ? restrictedModelSelection
      : selectedModelName}
  {#if modelName}
    <div class="space-y-1">
      <label class="label" for="selected-model">Model</label>
      <Input id="selected-model" value={modelName} disabled />
    </div>
  {/if}
{/snippet}

{#snippet dataset(
  name: 'unbiased' | 'biased' | 'utility' | null,
  dataset: IDataset,
  setDataset: (dataset: IDataset) => void,
  index: number,
  required: boolean = false,
)}
  {@const displayName =
    name === 'unbiased'
      ? 'Retain'
      : name === 'biased'
        ? 'Forget'
        : name === 'utility'
          ? 'Utility'
          : null}
  {#if window.airGapped}
    <label for="{name}-dataset-{index}">
      {#if displayName}
        {displayName} dataset type
      {:else}
        Dataset type
      {/if}
    </label>
    <Select
      id="{name}-dataset-{index}"
      value={dataset.type}
      onchange={(event) => {
        if (event.currentTarget.value === DatasetMetadataType.HirundoCSV) {
          setDataset(new HirundoCSV())
        } else if (event.currentTarget.value === DatasetMetadataType.HuggingFaceDataset) {
          setDataset(new HuggingFaceDataset())
        }
      }}
    >
      <option value={DatasetMetadataType.HirundoCSV}
        >{displayMetadataTypes[DatasetMetadataType.HirundoCSV]}</option
      >
      <option value={DatasetMetadataType.HuggingFaceDataset}
        >{displayMetadataTypes[DatasetMetadataType.HuggingFaceDataset]}</option
      >
    </Select>
  {/if}
  {#if dataset.type === DatasetMetadataType.HirundoCSV}
    {@const fieldName = name ? (`${name}DatasetCsvUrl` as const) : 'datasetCsvUrl'}
    <label class="pb-1" for={fieldName}>
      {#if displayName}
        {displayName} dataset CSV URL
      {:else}
        Dataset CSV URL
      {/if}
    </label>
    <MetadataFields
      prefix={getStoragePrefix({
        type: StorageType.Local,
      })}
      invalids={invalids[index]}
      {fieldName}
      fieldValue={dataset.csvUrl}
      setField={(newValue: string) => {
        if (dataset.type === DatasetMetadataType.HirundoCSV) {
          setDataset(new HirundoCSV(newValue))
        }
      }}
      {required}
    />
  {:else if dataset.type === DatasetMetadataType.HuggingFaceDataset}
    {@const fieldName = name
      ? (`${name}HuggingFaceDatasetName` as const)
      : 'huggingFaceDatasetName'}
    <HuggingFaceDatasetFields
      {dataset}
      fieldId={`${fieldName}-${index}`}
      label={displayName ? `${displayName} dataset name` : 'Dataset name'}
      invalidState={invalids[index][fieldName]}
      sharedTokenEnabled={sharedCustomDatasetTokenState.useToken}
      onNameChange={(huggingFaceDatasetName) => {
        if (dataset.type === DatasetMetadataType.HuggingFaceDataset) {
          setDataset(cloneHuggingFaceDataset(dataset, huggingFaceDatasetName))
        }
      }}
      {required}
    />
  {/if}
{/snippet}

{#snippet unlearningLlmAdvancedOptions(runInfo: RunInfo)}
  <Accordion class="card variant-glass-surface">
    <AccordionItem on:toggle={accordionChange} open={useAdvancedRunOptions} class="mt-4">
      {#snippet lead()}
        <Icon icon="iconoir:settings" class="h-6 w-6" />
      {/snippet}
      {#snippet summary()}
        <h3>Advanced run options</h3>
      {/snippet}
      {#snippet content()}
        <form class="space-y-4" bind:this={advancedOptionsFormRef}>
          {#snippet maxTokensForModel(datasetType: DatasetType)}
            <div class="grid grid-cols-[auto,1fr] gap-2">
              <div>
                <label class="label pb-2" for="max-tokens-for-model-{datasetType}"
                  >Max tokens for model (for {displayDatasetType[datasetType]} dataset):</label
                >
                <div class="input-group input-group-divider grid-cols-[1fr_auto] align-middle">
                  <input
                    id="max-tokens-for-model-{datasetType}"
                    name="max-tokens-for-model-{datasetType}"
                    type="number"
                    step="1"
                    min="0"
                    class="input my-0 box-border flex w-fit px-2 py-1"
                    value={runInfo.advancedOptions?.maxTokensForModel?.[datasetType]}
                    onchange={(event) => {
                      if (runInfo.advancedOptions && runInfo.advancedOptions.maxTokensForModel) {
                        runInfo.advancedOptions.maxTokensForModel[datasetType] = Number(
                          event.currentTarget.value,
                        )
                      } else {
                        runInfo.advancedOptions = new UnlearningLlmAdvancedOptions()
                        runInfo.advancedOptions.maxTokensForModel = {
                          [DatasetType.BIAS]: MAX_TOKENS_FOR_MODEL,
                          [DatasetType.UNBIAS]: MAX_TOKENS_FOR_MODEL,
                          [DatasetType.NORMAL]: MAX_TOKENS_FOR_MODEL,
                        }
                        runInfo.advancedOptions.maxTokensForModel[datasetType] = Number(
                          event.currentTarget.value,
                        )
                      }
                    }}
                  />
                  <div>tokens</div>
                </div>
              </div>
            </div>
          {/snippet}
          {@render maxTokensForModel(DatasetType.BIAS)}
          {@render maxTokensForModel(DatasetType.UNBIAS)}
          {@render maxTokensForModel(DatasetType.NORMAL)}
        </form>
      {/snippet}
    </AccordionItem>
  </Accordion>
{/snippet}

{#snippet footer()}
  <footer
    class="modal-footer sticky bottom-0 bg-surface-50 px-4 py-2 dark:bg-surface-800 {parent.regionFooter}"
  >
    <div class="flex w-full items-center gap-2">
      {#if canReturnToRunOptions}
        <button class="variant-filled-surface btn" onclick={onBackToRunOptions}>
          Back to run options
        </button>
      {/if}
      {#if multipleUnlearnTypes}
        <button class="variant-filled-surface btn" onclick={() => changeUnlearnType(null)}>
          Change unlearning type
        </button>
      {/if}
      <div class="variant-filled-primary btn-group ml-auto flex w-min items-center">
        <button class={parent.buttonNeutral} onclick={onFormCancel}>
          {parent.buttonTextCancel}
        </button>
        <button
          class={parent.buttonPositive}
          onclick={onFormSubmit}
          disabled={config === null ||
            (unlearnType === UnlearnType.BEHAVIOR && enabledBehaviors.length === 0)}
        >
          {parent.buttonTextConfirm}
        </button>
      </div>
    </div>
  </footer>
{/snippet}

{#if $modalStore[0]}
  <div
    class="card w-modal {multipleUnlearnTypes
      ? 'min-w-[42rem]'
      : ''} relative max-h-[90vh] space-y-4 overflow-y-scroll border border-surface-500 shadow-xl"
  >
    <h3
      class="h3 sticky top-0 z-10 justify-between bg-surface-50 px-4 pb-2 pt-4 dark:bg-surface-800"
    >
      {$modalStore[0].title}
      <CloseModalButton onClose={parent.onClose} />
    </h3>

    {#if unlearnType === UnlearnType.BEHAVIOR}
      <article class="space-y-4 overflow-y-hidden p-4 rounded-container-token">
        {$modalStore[0].body}
        {@render modelNoticeIfNecessary(
          isRestrictedMode,
          restrictedModelIds,
          restrictedModelSelection,
          selectedModel?.modelName ?? selectedModelSourceName,
        )}
        <form class="mt-2 space-y-1" bind:this={runInfoFormRef}>
          {#if restoreWarning}
            <p role="alert" class="rounded-container variant-soft-warning p-3">{restoreWarning}</p>
          {/if}
          <div class="grid gap-2 p-2">
            {#if configLoadFailed}
              <p role="alert">Could not load behavior availability. Please try again later.</p>
            {:else if config === null}
              <p role="status">Loading behavior availability…</p>
            {:else if enabledBehaviors.length === 0}
              <p role="alert">No behavior unlearning types are enabled for this deployment.</p>
            {/if}
            <SharedCustomDatasetTokenSection
              state={sharedCustomDatasetTokenState}
              visible={hasCustomHuggingFaceDatasets}
            />
            {#each runInfo.targetBehaviors as behavior, i (i)}
              {@const canSelectSecurity =
                runInfo.targetBehaviors.length === 0 ||
                !runInfo.targetBehaviors.some(
                  (target, targetIndex) => target instanceof SecurityBehavior && targetIndex !== i,
                )}
              <div class="card space-y-2 px-4 py-2">
                <h3 class="h3">
                  <span class="align-middle">Behavior</span>
                  <span class="variant-filled badge">{i + 1}</span>
                </h3>
                <p>Behavior type</p>
                <div>
                  {#if enabledBehaviors.includes(BehaviorType.BIAS)}
                    <label class="flex items-center space-x-2" for="behavior-type-{i}-bias">
                      <input
                        type="radio"
                        id="behavior-type-{i}-bias"
                        name="behavior-type-{i}-bias"
                        checked={behavior.type === BehaviorType.BIAS}
                        onchange={() => {
                          runInfo.targetBehaviors[i] = getBehaviorByType(BehaviorType.BIAS, {
                            biasType: BiasType.ALL,
                          })
                          restoreWarning = null
                        }}
                      />
                      <p>Bias</p>
                    </label>
                  {/if}
                  <!-- <label class="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="behavior-type-{i}"
                      checked={behavior.type === BehaviorType.HALLUCINATION}
                      onchange={() => {
                        runInfo.targetBehaviors[i] = getBehaviorByType(BehaviorType.HALLUCINATION, {
                          hallucinationType:
                            runInfo.targetBehaviors[i] instanceof HallucinationsBehavior
                              ? runInfo.targetBehaviors[i].hallucinationType
                              : HallucinationType.GENERAL,
                        })
                      }}
                    />
                    <p>Hallucinations</p>
                  </label> -->
                  <!-- TODO: Add hallucinations back in when unlearn LLM hallucinations is ready -->
                  {#if enabledBehaviors.includes(BehaviorType.SECURITY)}
                    <label class="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="behavior-type-{i}"
                        checked={behavior.type === BehaviorType.SECURITY}
                        disabled={!canSelectSecurity}
                        onchange={() => {
                          runInfo.targetBehaviors[i] = getBehaviorByType(BehaviorType.SECURITY)
                          restoreWarning = null
                        }}
                      />
                      <p class={canSelectSecurity ? '' : 'text-surface-400'}>Security</p>
                      {#if !canSelectSecurity}
                        <button
                          disabled
                          class="[&>*]:pointer-events-none"
                          use:popup={getInvalidBehaviorHover(`invalidSecurityBehaviorHover-${i}`)}
                        >
                          <Icon icon="iconoir:info-circle-solid" class="h-4 w-4" />
                        </button>
                      {/if}
                      <div
                        data-popup={`invalidSecurityBehaviorHover-${i}`}
                        class="card variant-filled-surface z-50 p-2"
                      >
                        <span
                          >Cannot select the security behavior if security has already been selected</span
                        >
                      </div>
                    </label>
                  {/if}
                  {#if enabledBehaviors.includes(BehaviorType.REFUSAL)}
                    <label class="flex items-center space-x-2" for="behavior-type-{i}-refusal">
                      <input
                        type="radio"
                        id="behavior-type-{i}-refusal"
                        name="behavior-type-{i}"
                        checked={behavior.type === BehaviorType.REFUSAL}
                        onchange={() => {
                          runInfo.targetBehaviors[i] = getBehaviorByType(BehaviorType.REFUSAL)
                          runInfo.targetUtilities = []
                          runInfo.aggressiveness = null
                          restoreWarning = null
                        }}
                      />
                      <p>Refusal</p>
                    </label>
                  {/if}
                  {#if enabledBehaviors.includes(BehaviorType.CUSTOM)}
                    <label class="flex items-center space-x-2" for="behavior-type-{i}-custom">
                      <input
                        type="radio"
                        id="behavior-type-{i}-custom"
                        name="behavior-type-{i}-custom"
                        checked={behavior.type === BehaviorType.CUSTOM}
                        onchange={() => {
                          runInfo.targetBehaviors[i] = getBehaviorByType(BehaviorType.CUSTOM)
                          restoreWarning = null
                        }}
                      />
                      <p>Custom</p>
                    </label>
                  {/if}
                </div>
                {#if behavior.type === BehaviorType.CUSTOM}
                  {@const biasedDataset = behavior.biasedDataset}
                  {@const unbiasedDataset = behavior.unbiasedDataset}
                  <div class="space-y-3">
                    <p class="font-medium">Forget dataset</p>
                    {@render dataset(
                      'biased',
                      biasedDataset,
                      (dataset) => {
                        behavior.biasedDataset = dataset
                      },
                      i,
                      true,
                    )}
                    <p class="font-medium">Retain dataset</p>
                    {@render dataset(
                      'unbiased',
                      unbiasedDataset,
                      (dataset) => {
                        behavior.unbiasedDataset = dataset
                      },
                      i,
                      true,
                    )}
                  </div>
                {:else if behavior.type === BehaviorType.HALLUCINATION}
                  <label for="hallucination-type">Hallucination type</label>
                  <Select
                    id="hallucination-type"
                    value={behavior.hallucinationType}
                    onchange={(event) => {
                      behavior.hallucinationType = event.currentTarget.value as HallucinationType
                    }}
                  >
                    {#each hallucinationTypeValues as hallucinationType (hallucinationType)}
                      <option value={hallucinationType}
                        >{hallucinationTypeLabels[hallucinationType]}</option
                      >
                    {/each}
                  </Select>
                {/if}
                {#if behavior.type !== BehaviorType.REFUSAL}
                  <div class="space-y-2">
                    <p class="font-medium">Utility datasets</p>
                    <p class="text-sm text-surface-700 dark:text-surface-200">
                      The default utility dataset is always included.
                    </p>
                    {#each behaviorCustomUtilities as utility, customUtilityIndex (customUtilityIndex)}
                      {#if utility.dataset}
                        <div
                          class="space-y-2 border border-surface-200 p-3 rounded-container-token"
                        >
                          <p class="font-medium">
                            Additional utility dataset {customUtilityIndex + 1}
                          </p>
                          {@render dataset(
                            'utility',
                            utility.dataset,
                            (dataset) => {
                              runInfo.targetUtilities[customUtilityIndex].dataset = dataset
                            },
                            customUtilityIndex + 1,
                            true,
                          )}
                          <button
                            class="variant-filled-error btn"
                            type="button"
                            onclick={() => {
                              removeBehaviorCustomUtility(customUtilityIndex)
                            }}>Remove utility dataset</button
                          >
                        </div>
                      {/if}
                    {/each}
                    <button
                      class="variant-filled btn"
                      type="button"
                      onclick={() => {
                        addBehaviorCustomUtility()
                      }}>Add utility dataset</button
                    >
                  </div>
                {/if}
              </div>
            {/each}
          </div>
          {#if runInfo.targetBehaviors[0]?.type !== BehaviorType.REFUSAL}
            <div class="mt-2 space-y-2">
              <div class="space-y-1">
                <label for="aggressiveness">Aggressiveness</label>
                <Input
                  id="aggressiveness"
                  type="number"
                  min="0.001"
                  max="1"
                  step="0.001"
                  value={runInfo.aggressiveness ?? 1}
                  required
                  onchange={(event) => {
                    const parsedAggressiveness = Number(event.currentTarget.value)
                    runInfo.aggressiveness = Number.isFinite(parsedAggressiveness)
                      ? parsedAggressiveness
                      : null
                  }}
                />
              </div>
            </div>
          {/if}
          {#if shouldShowAdvancedRunOptions}
            {@render unlearningLlmAdvancedOptions(runInfo)}
          {/if}
        </form>
      </article>
      {@render footer()}
    {:else if unlearnType === UnlearnType.DATA}
      <article class="space-y-4 overflow-y-hidden p-4 rounded-container-token">
        {$modalStore[0].body}
        {@render modelNoticeIfNecessary(
          isRestrictedMode,
          restrictedModelIds,
          restrictedModelSelection,
          selectedModel?.modelName ?? selectedModelSourceName,
        )}
        <form class="mt-2 space-y-1" bind:this={runInfoFormRef}>
          <div class="grid gap-2 p-2">
            <SharedCustomDatasetTokenSection
              state={sharedCustomDatasetTokenState}
              visible={hasCustomHuggingFaceDatasets}
            />
            {#each runInfo.targetUtilities as target, i (i)}
              {#if target.dataset}
                <div class="card space-y-2 px-4 py-2">
                  <h3 class="h3">Dataset {i + 1}</h3>
                  <div>
                    {@render dataset(
                      null,
                      target.dataset,
                      (dataset) => {
                        target.dataset = dataset
                      },
                      i,
                      true,
                    )}
                    {#if runInfo.targetUtilities.length > 1}
                      <button
                        class="variant-filled-error btn"
                        onclick={() => {
                          runInfo.targetUtilities.splice(i, 1)
                        }}>Remove dataset</button
                      >
                    {/if}
                  </div>
                </div>
              {/if}
            {/each}
          </div>
          <button
            class="variant-filled btn"
            onclick={() => {
              runInfo.targetUtilities.push(new CustomUtility(new HuggingFaceDataset()))
              invalids.push(INITIAL_INVALID_STATE)
            }}>Add another dataset</button
          >
          {#if shouldShowAdvancedRunOptions}
            {@render unlearningLlmAdvancedOptions(runInfo)}
          {/if}
        </form>
      </article>
      {@render footer()}
    {:else}
      <article class="space-y-4 p-4 rounded-container-token">
        {$modalStore[0].body}
        <div class="my-4 grid grid-cols-2 justify-items-center">
          <button
            class="flex flex-col"
            onclick={() => {
              changeUnlearnType(UnlearnType.BEHAVIOR)
            }}
          >
            <span
              class="unlearn-type-icon variant-filled btn-icon btn-icon-xl border-2"
              aria-label="Brain icon indicating behavior unlearning"
            >
              <NounBehavior />
            </span>
            <span>Behavior</span>
          </button>
          <button
            class="flex flex-col"
            onclick={() => {
              changeUnlearnType(UnlearnType.DATA)
            }}
          >
            <span
              class="unlearn-type-icon variant-filled btn-icon btn-icon-xl border-2"
              aria-label="Remove row icon indicating data unlearning"
            >
              <NounRemoveRow />
            </span>
            <span>Data</span>
          </button>
        </div>
      </article>
    {/if}
  </div>
{/if}

<style lang="postcss">
  /* Inline SVG icon styling for unlearn type buttons */
  .unlearn-type-icon {
    @apply flex items-center justify-center;
  }

  .unlearn-type-icon :global(svg) {
    @apply h-8 w-8;
  }
</style>
